import { findGame, pageTitle, SITE, type GameSummary, type GameType } from '@cardengine/shared';
import { useEffect, useState } from 'react';
import { Cabinet, isCabinet } from './arcade/Cabinet.js';
import { loadDeckArt } from './cards/deckArt.js';
import { findCardBack } from './cardBacks.js';
import { PubDressing } from './components/PubDressing.js';
import { SiteNav } from './components/SiteNav.js';
import { ConnectionBanner, Toasts } from './components/Toasts.js';
import { linkProps, navigate, TABLE_PATH, usePath } from './router.js';
import { useStore } from './store.js';
import { unlockAudio } from './sound.js';
import { useTurnAlerts } from './useTurnAlerts.js';
import { DescentHowToPlay } from './views/DescentHowToPlay.js';
import { FreecellHowToPlay } from './views/FreecellHowToPlay.js';
import { FreecellRoom } from './views/FreecellRoom.js';
import { GamePage } from './views/GamePage.js';
import { GameRoom } from './views/GameRoom.js';
import { HowToPlay } from './views/HowToPlay.js';
import { LobbyRoom } from './views/LobbyRoom.js';
import { MinesweeperHowToPlay } from './views/MinesweeperHowToPlay.js';
import { MinesweeperRoom } from './views/MinesweeperRoom.js';
import { NoughtsHowToPlay } from './views/NoughtsHowToPlay.js';
import { NoughtsRoom } from './views/NoughtsRoom.js';
import { Privacy } from './views/Privacy.js';
import { SiteHome } from './views/SiteHome.js';
import { SolitaireHowToPlay } from './views/SolitaireHowToPlay.js';
import { SolitaireRoom } from './views/SolitaireRoom.js';
import { SpiderHowToPlay } from './views/SpiderHowToPlay.js';
import { SpiderRoom } from './views/SpiderRoom.js';
import { StackHowToPlay } from './views/StackHowToPlay.js';

/*
 * Each game's rules are its own page — a shared template would be mostly holes
 * by the fourth one. Looked up by catalogue id rather than chained through
 * ternaries, which stopped reading at three.
 */
const RULES_PAGES: Record<string, (game: GameSummary) => React.ReactElement> = {
  solitaire: (game) => <SolitaireHowToPlay game={game} />,
  freecell: (game) => <FreecellHowToPlay game={game} />,
  spider: (game) => <SpiderHowToPlay game={game} />,
  'noughts-and-crosses': (game) => <NoughtsHowToPlay game={game} />,
  minesweeper: (game) => <MinesweeperHowToPlay game={game} />,
  stack: (game) => <StackHowToPlay game={game} />,
  descent: (game) => <DescentHowToPlay game={game} />,
};

/** The table each game is played at. Anything unlisted is a Switch table. */
const GAME_ROOMS: Partial<Record<GameType, () => React.ReactElement>> = {
  solitaire: () => <SolitaireRoom />,
  freecell: () => <FreecellRoom />,
  spider: () => <SpiderRoom />,
  'noughts-and-crosses': () => <NoughtsRoom />,
  minesweeper: () => <MinesweeperRoom />,
};

function NotFound() {
  return (
    <main className="page page--prose">
      <header className="prose__head">
        <p className="prose__eyebrow">404</p>
        <h1 className="prose__title">Nothing dealt here</h1>
        <p className="prose__lead">That page does not exist.</p>
      </header>
      <div className="prose__cta">
        <a className="btn btn--primary" {...linkProps('/')}>
          Back to {SITE.name}
        </a>
      </div>
    </main>
  );
}

/** Resolves the current path to a page and the title that goes with it. */
function useSitePage(path: string) {
  const segments = path.split('/').filter(Boolean);
  const game = segments[0] ? findGame(segments[0]) : undefined;

  if (segments.length === 0) return { element: <SiteHome />, title: pageTitle() };
  if (segments.length === 1 && segments[0] === 'privacy') {
    return { element: <Privacy />, title: pageTitle('Privacy') };
  }
  /*
   * A cabinet has no table to sit at: it is one player against a machine, all
   * of it in the browser. So its address goes straight to the game rather than
   * to a landing page whose only button would be "start", which is what every
   * other game needs because every other game has a lobby behind it.
   */
  if (game && segments.length === 1 && isCabinet(game.id)) {
    return { element: <Cabinet game={game} />, title: pageTitle(game.name) };
  }
  if (game && segments.length === 1) return { element: <GamePage game={game} />, title: pageTitle(game.name) };
  if (game && segments[1] === 'how-to-play' && segments.length === 2) {
    const rules = (RULES_PAGES[game.id] ?? ((g: GameSummary) => <HowToPlay game={g} />))(game);
    return { element: rules, title: pageTitle(`How to play ${game.name}`) };
  }
  return { element: <NotFound />, title: pageTitle('Not found') };
}

export function App() {
  const screen = useStore((s) => s.screen);
  const gameType = useStore((s) => s.lobby?.gameType);
  const connect = useStore((s) => s.connect);
  const path = usePath();
  const page = useSitePage(path);

  useEffect(() => {
    connect();
  }, [connect]);

  // The deck artwork is its own chunk. Start it now so it is in hand well
  // before anyone sits down; a failure here only costs blank stock, and the
  // next mount tries again.
  useEffect(() => {
    void loadDeckArt().catch(() => {});
  }, []);

  /*
   * Wake the audio context on the first interaction of any kind.
   *
   * Browsers hand back a suspended AudioContext until the page has been
   * interacted with, and `resume()` is asynchronous — so the first sound of a
   * session used to start the resume and then find the context still
   * suspended a line later, and drop itself. Nothing else unlocked it either:
   * the only caller was the sound checkbox, which most people never touch.
   * The upshot was that whichever sound came first in a session was always
   * silent, which is a maddening bug to chase because the second one works.
   */
  useEffect(() => {
    const wake = () => unlockAudio();
    const options = { once: true, passive: true } as const;
    window.addEventListener('pointerdown', wake, options);
    window.addEventListener('keydown', wake, options);
    return () => {
      window.removeEventListener('pointerdown', wake);
      window.removeEventListener('keydown', wake);
    };
  }, []);

  // The chosen card back is pushed onto the document as custom properties, so
  // every card back on screen picks it up without prop-drilling.
  const cardBack = useStore((s) => s.cardBack);
  useEffect(() => {
    const back = findCardBack(cardBack);
    document.documentElement.style.setProperty('--back-from', back.from);
    document.documentElement.style.setProperty('--back-to', back.to);
  }, [cardBack]);

  // Sitting at a table takes over the whole window, so the title follows that
  // rather than whatever page the address bar happens to be showing.
  const inLobby = screen === 'lobby';
  const inGame = screen === 'game';
  const seated = inLobby || inGame;

  // Chime, and keep the screen awake while seated.
  const { isMyTurn } = useTurnAlerts();

  // The title is the one nudge that works for someone in another tab, so it
  // has to react to the tab being hidden as well as to whose turn it is.
  const [hidden, setHidden] = useState(() => document.hidden);
  useEffect(() => {
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    document.title = inGame
      ? pageTitle(isMyTurn && hidden ? 'Your turn' : 'Playing')
      : inLobby
        ? pageTitle('Lobby')
        : page.title;
  }, [inGame, inLobby, page.title, isMyTurn, hidden]);

  // An invite link lands on the site root; send it to the game's own page with
  // the code intact so the join form can pick it up.
  useEffect(() => {
    if (path !== '/') return;
    const code = new URLSearchParams(window.location.search).get('join');
    if (code) navigate(`/switch?join=${encodeURIComponent(code)}`, { replace: true });
  }, [path]);

  // Holding a seat but looking at a site page is fine — the banner keeps the
  // way back obvious. Landing on /table without a seat is not.
  useEffect(() => {
    if (path === TABLE_PATH && !seated) navigate('/', { replace: true });
  }, [path, seated]);

  const atTable = path === TABLE_PATH && seated;

  // The table sizes itself to the viewport and brings its own chrome, so a
  // full nav bar would eat into it; its header carries a link home instead.
  if (atTable && inGame) {
    return (
      <div className="app app--table">
        <ConnectionBanner />
        {(gameType && GAME_ROOMS[gameType] ? GAME_ROOMS[gameType]! : () => <GameRoom />)()}
        <Toasts />
      </div>
    );
  }

  if (atTable && inLobby) {
    return (
      <div className="app">
        <ConnectionBanner />
        <SiteNav />
        <LobbyRoom />
        <Toasts />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Behind everything, and only on the site pages — a table in play has
          no spare margin and needs none of this. */}
      <PubDressing />
      <ConnectionBanner />
      <SiteNav />
      {seated && <SeatedBanner />}
      {page.element}
      <SiteFooter />
      <Toasts />
    </div>
  );
}

/**
 * Shown while browsing the site with a seat still held, so wandering off can
 * never quietly cost someone their place at a live table.
 */
function SeatedBanner() {
  const lobby = useStore((s) => s.lobby);
  const inGame = useStore((s) => s.screen === 'game');
  const leaveLobby = useStore((s) => s.leaveLobby);

  return (
    <div className="seated">
      <span className="seated__text">
        {inGame ? 'A game is in progress at' : "You're still seated at"} table{' '}
        <strong>{lobby?.code ?? '…'}</strong>
      </span>
      <span className="seated__actions">
        <button type="button" className="btn btn--ghost btn--small" onClick={() => void leaveLobby()}>
          Leave table
        </button>
        <button
          type="button"
          className="btn btn--primary btn--small"
          onClick={() => navigate(TABLE_PATH)}
        >
          Back to table
        </button>
      </span>
    </div>
  );
}

function SiteFooter() {
  return (
    <footer className="sitefooter">
      <span className="sitefooter__mark" aria-hidden="true">
        ♠ ♥ ♦ ♣
      </span>
      <p>
        {SITE.name} · {SITE.blurb}
      </p>
      <p className="sitefooter__credit">
        <a {...linkProps('/privacy')}>Privacy</a> · Card artwork by{' '}
        <a href="https://badhandstudio.itch.io/high-resolution-playing-cards" rel="noreferrer noopener" target="_blank">
          Bad Hand Studio
        </a>
      </p>
    </footer>
  );
}
